import axios from 'axios';
import { API_CONFIG, buildAuthApiUrl, getAuthHeaders } from '../config/api';
import secureStorage from '../utils/secureStorage';
import publicAuthAxios from '../utils/publicAuthAxios';
import smartSearchService from './smartSearchService';

// Auth UI flows (login / sign up / verify) render errors inside AuthPanel.
// Use publicAuthAxios (no global JWT interceptor) so stale tokens are never sent.
const AUTH_LOCAL_ONLY_HEADERS = {
  'X-Suppress-Global-Notification': 'true',
} as const;

export type ClientTypeCode = 'C' | 'B';

// Email verification interfaces
export interface SendEmailCodeRequest {
  clientType: ClientTypeCode;
  email: string;
}

export interface SendEmailCodeResponse {
  success: boolean;
  code: string;
  message: string;
  details?: any;
  exceptionRequestId?: string;
  exceptionTimestamp?: string;
}

export interface VerifyEmailCodeRequest {
  clientType: ClientTypeCode;
  email: string;
  emailCode: string;
}

export interface VerifyEmailCodeResponse {
  success: boolean;
  code: string;
  message: string;
  details?: any;
  exceptionRequestId?: string;
  exceptionTimestamp?: string;
}

export interface EmailRegisterRequest {
  clientType: ClientTypeCode;
  email: string;
  password: string;
  confirmPassword: string;
  emailCode: string;
  nickname?: string;
  // Registration extensions: role is B-side only; phone is optional for C-side and required for B-side.
  role?: number;
  areaCode?: string;
  phone?: string;
}

export interface EmailRegisterResponse {
  success: boolean;
  code: string;
  message: string;
  details?: {
    accessToken: string;
    refreshToken: string;
  };
  exceptionRequestId?: string;
  exceptionTimestamp?: string;
}

export interface AuthResponse {
  token: string;
  tokenType?: string;
  userId: string;
  userName?: string;
  nickName?: string;
  email?: string;
  phoneNumber?: string;
  firstName: string;
  lastName: string;
  role: 'CUSTOMER' | 'BROKER' | 'ADMIN';
  clientType?: 'CUSTOMER' | 'BROKER';
}

export interface LogoutResponse {
  success: boolean;
  code: string;
  message: string;
  details: any;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

export interface EmailLoginRequest {
  clientType: ClientTypeCode;
  email: string;
  password: string;
  enableRememberMe?: boolean;
}

export interface EmailLoginResponse {
  success: boolean;
  code: string;
  message: string;
  details?: {
    accessToken: string;
    refreshToken?: string;
  };
  exceptionRequestId?: string;
  exceptionTimestamp?: string;
}

export interface ForgotPasswordRequest {
  clientType: ClientTypeCode;
  email: string;
  emailCode: string;
  password: string;
  confirmPassword: string;
}

export interface ForgotPasswordResponse {
  success: boolean;
  code: string;
  message: string;
  details?: any;
  exceptionRequestId?: string;
  exceptionTimestamp?: string;
}

export interface RefreshTokenResponse {
  success: boolean;
  code: string;
  message: string;
  details?: {
    accessToken: string;
    refreshToken?: string;  // Optional: backend may rotate refresh token
  };
  exceptionRequestId?: string;
  exceptionTimestamp?: string;
}

export interface CompleteThirdPartyRegistrationRequest {
  nickname: string;
  password: string;
  confirmPassword: string;
}

export interface CompleteThirdPartyRegistrationResponse {
  success: boolean;
  code: string;
  message: string;
  details?: {
    accessToken: string;
    refreshToken?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    userId?: string;
    role?: AuthResponse['role'];
    clientType?: AuthResponse['clientType'] | ClientTypeCode;
  };
  exceptionRequestId?: string;
  exceptionTimestamp?: string;
}

export interface GoogleVerifyResponseDetails {
  accessToken: string;
  refreshToken?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  nickName?: string;
  userId?: string;
  role?: AuthResponse['role'];
  clientType?: AuthResponse['clientType'] | ClientTypeCode;
}

export interface GoogleVerifyResponse {
  success: boolean;
  code: string;
  message: string;
  details?: GoogleVerifyResponseDetails;
  exceptionRequestId?: string | null;
  exceptionTimestamp?: string | null;
}

const LETTER_CHAR_RE = /\p{L}/u;

const lettersIn = (value: string): string[] => (
  Array.from(value).filter(char => LETTER_CHAR_RE.test(char))
);

const initialsFromLetters = (value: string, count: number): string => (
  lettersIn(value).slice(0, count).join('').toUpperCase()
);

// Helper to extract initials from firstName and lastName.
// Only Unicode letters are used, so digits from emails, user IDs, or numeric
// nicknames never become avatar text. Returns empty string if no valid letters.
export const getInitialsFromName = (firstName: string | undefined | null, lastName: string | undefined | null, nickname: string | undefined | null = null): string => {
  const firstRaw = (firstName || '').trim();
  const lastRaw = (lastName || '').trim();
  const first = initialsFromLetters(firstRaw, 1);
  const last = initialsFromLetters(lastRaw, 1);

  if (first && last) {
    return first + last;
  }
  if (first) {
    return initialsFromLetters(firstRaw, 2);
  }
  if (last) {
    return initialsFromLetters(lastRaw, 2);
  }

  const nick = (nickname || '').trim();
  if (!nick) {
    return '';
  }

  const parts = nick.split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    const start = initialsFromLetters(parts[0], 1);
    const end = initialsFromLetters(parts[parts.length - 1], 1);
    if (start && end) {
      return start + end;
    }
  }

  return initialsFromLetters(nick, 2);
};

export type AvatarNameSource = {
  firstName?: string | null;
  lastName?: string | null;
  nickname?: string | null;
  nickName?: string | null;
};

// Profile names win over the sparse login user object. Email is never used.
export const getSignedInAvatarInitials = (
  profile?: AvatarNameSource | null,
  user?: AvatarNameSource | null,
): string => (
  getInitialsFromName(
    profile?.firstName || user?.firstName,
    profile?.lastName || user?.lastName,
    profile?.nickname || profile?.nickName || user?.nickname || user?.nickName,
  ) || 'U'
);

class AuthService {
  private persistAuthSession(
    details: GoogleVerifyResponseDetails,
    options: {
      clientType: AuthResponse['clientType'] | ClientTypeCode;
      fallbackEmail?: string;
      role?: AuthResponse['role'];
    }
  ): AuthResponse {
    secureStorage.setToken(details.accessToken);

    if (details.refreshToken) {
      secureStorage.setItem('refreshToken', details.refreshToken);
    }

    const normalizeClientType = (value?: AuthResponse['clientType'] | ClientTypeCode): AuthResponse['clientType'] | undefined => {
      if (!value) return undefined;
      if (value === 'C' || value === 'CUSTOMER') return 'CUSTOMER';
      if (value === 'B' || value === 'BROKER') return 'BROKER';
      return undefined;
    };

    const normalizedClientType = normalizeClientType(details.clientType) ?? normalizeClientType(options.clientType) ?? 'CUSTOMER';
    const inferredRole: AuthResponse['role'] =
      details.role
        ?? options.role
        ?? (normalizedClientType === 'BROKER'
          ? 'BROKER'
          : 'CUSTOMER');

    const authData: AuthResponse = {
      token: details.accessToken,
      tokenType: 'Bearer',
      userId: details.userId ?? '0',
      email: details.email ?? options.fallbackEmail,
      firstName: details.firstName ?? '',
      lastName: details.lastName ?? '',
      nickName: details.nickName?.trim() || this.extractNickNameFromToken(details.accessToken),
      role: inferredRole,
      clientType: normalizedClientType,
    };

    secureStorage.setUserData(authData);
    return authData;
  }

  private getAuthHeader() {
    return getAuthHeaders();
  }

  getCurrentUser(): AuthResponse | null {
    return secureStorage.getUserData();
  }

  getToken(): string | null {
    return secureStorage.getToken();
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  isAdmin(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'ADMIN';
  }

  isBroker(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'BROKER' || user?.clientType === 'BROKER';
  }

  isCustomer(): boolean {
    const user = this.getCurrentUser();
    return user?.role === 'CUSTOMER' || user?.clientType === 'CUSTOMER';
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role === role || user?.clientType === role;
  }

  async sendEmailCode(data: SendEmailCodeRequest): Promise<SendEmailCodeResponse> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.EMAIL_CODE_SEND);
      console.log('🔵 Sending email code to URL:', url);
      console.log('🔵 Request data:', data);
      const response = await publicAuthAxios.post(url, data, { headers: AUTH_LOCAL_ONLY_HEADERS });
      console.log('✅ Email code sent successfully:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ Failed to send email code:', error);
      console.error('❌ Full error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      
      // Handle rate limiting errors
      if (error.response?.status === 400 || error.response?.status === 429) {
        const errorData = error.response.data;
        const backendMessage = errorData?.message || '';
        
        // Check if it's a rate limit error (Chinese or English)
        if (/频繁|frequent|wait|等待|minute|分钟|too many|limit/i.test(backendMessage)) {
          throw new Error('Please wait 1 minute before requesting another verification code');
        }
        
        // If not rate limit, show the backend message or fallback
        throw new Error(errorData?.message || 'Invalid email address');
      } else if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to send email code');
      } else if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      } else {
        throw new Error('An unexpected error occurred while sending email code');
      }
    }
  }

  async verifyEmailCode(data: VerifyEmailCodeRequest): Promise<VerifyEmailCodeResponse> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.EMAIL_CODE_VERIFY);
      const response = await publicAuthAxios.post(url, data, { headers: AUTH_LOCAL_ONLY_HEADERS });
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        const errorData: VerifyEmailCodeResponse = error.response.data;
        if (errorData.code === 'U016') {
          throw new Error('Your verification code has expired. Please request a new one.');
        }
        if (errorData.code === 'U017') {
          throw new Error('Incorrect verification code.');
        }
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      }

      if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to verify email code');
      }
      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }
      throw new Error(error.message || 'An unexpected error occurred while verifying email code');
    }
  }

  async registerWithEmailCode(data: EmailRegisterRequest): Promise<AuthResponse> {
    try {
      await this.ensureLoggedOutBeforeNewAuth();
      const response = await publicAuthAxios.post(buildAuthApiUrl(API_CONFIG.ENDPOINTS.EMAIL_REGISTER), data, {
        headers: AUTH_LOCAL_ONLY_HEADERS,
      });
      const responseData: EmailRegisterResponse = response.data;
      
      if (responseData.success && responseData.details?.accessToken) {
        const fallbackRole: AuthResponse['role'] =
          data.clientType === 'B'
            ? 'BROKER'
            : 'CUSTOMER';

        const accessToken = responseData.details.accessToken;
        const refreshToken = responseData.details.refreshToken;

        // Extract userId from the JWT token
        const userId = this.extractUserIdFromToken(accessToken);
        
        console.log('🔍 [REGISTER] Extracted userId:', userId);

        const authData = this.persistAuthSession(
          {
            accessToken: accessToken,
            refreshToken: refreshToken,
            email: data.email,
            // IMPORTANT: persist the same clientType used for registration
            clientType: data.clientType,
            userId: userId,
            firstName: '',
            lastName: '',
            role: fallbackRole
          },
          {
            clientType: data.clientType,
            fallbackEmail: data.email,
            role: fallbackRole,
          }
        );

        return authData;
      } else {
        // Handle error responses
        throw new Error(responseData.message || 'Registration failed');
      }
    } catch (error: any) {
      console.error('Failed to register with email code:', error);
      
      // Check for specific error codes
      if (error.response?.data) {
        const errorData: EmailRegisterResponse = error.response.data;
        
        if (errorData.code === 'U002') {
          throw new Error('Your passwords don\'t match. Please check and try again.');
        } else if (errorData.code === '-1') {
          const backendMessage = errorData.message || '';
          const requirements: string[] = [];

          if (/长度至少|at least 8/i.test(backendMessage)) {
            requirements.push('be at least 8 characters');
          }
          if (/大写|uppercase/i.test(backendMessage)) {
            requirements.push('include an uppercase letter');
          }
          if (/小写|lowercase/i.test(backendMessage)) {
            requirements.push('include a lowercase letter');
          }
          if (/数字|number/i.test(backendMessage)) {
            requirements.push('include a number');
          }

          if (requirements.length) {
            const lastRequirement = requirements.pop();
            const requirementsText = requirements.length
              ? `${requirements.join(', ')}${requirements.length > 1 ? ',' : ''} and ${lastRequirement}`
              : lastRequirement;
            throw new Error(`Password must ${requirementsText}.`);
          }
          // Backend sometimes returns a message that doesn't match our simple parser.
          // Prefer surfacing the backend's original message for accuracy.
          if (backendMessage.trim()) {
            throw new Error(backendMessage);
          }
          throw new Error('Password requirements not met. Please include uppercase, lowercase, number, and at least 8 characters.');
        } else if (errorData.code === 'U016') {
          throw new Error('Your verification code has expired. Please request a new one.');
        } else if (errorData.message) {
          throw new Error(errorData.message);
        }
      }
      
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid registration data');
      } else if (error.response?.status === 409) {
        throw new Error('An account with this email already exists');
      } else if (error.response) {
        throw new Error(error.response.data?.message || 'Registration failed');
      } else if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      } else {
        throw new Error(error.message || 'An unexpected error occurred during registration');
      }
    }
  }

  async logout(): Promise<void> {
    try {
      // Clean up smart search client-side session storage
      // Note: Backend session will auto-expire after 24 hours
      smartSearchService.cleanupSession();

      // Logout from auth service
      await axios.delete(buildAuthApiUrl(API_CONFIG.ENDPOINTS.AUTH_LOGOUT), {
        headers: {
          ...API_CONFIG.HEADERS,
          ...getAuthHeaders(),
          // Don't show global toast notifications for logout failures.
          // Logout should still succeed locally (token/state cleared).
          ...AUTH_LOCAL_ONLY_HEADERS,
        },
      });
    } catch (error: any) {
      console.error('Failed to logout:', error);
      throw error;
    } finally {
      secureStorage.clearAll();
    }
  }

  private decodeJwtPayload(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      return JSON.parse(atob(parts[1]));
    } catch {
      return null;
    }
  }

  // Helper function to decode JWT token and extract userId
  private extractUserIdFromToken(token: string): string {
    const parsedPayload = this.decodeJwtPayload(token);
    if (!parsedPayload) {
      console.warn('Invalid JWT token format');
      return '0';
    }

    // Extract userId from 'sub' field (standard JWT subject field)
    // Keep as string to avoid large number precision loss
    const userId = parsedPayload.sub ? String(parsedPayload.sub) : '0';

    console.log('🔍 [JWT] Extracted userId from token:', userId);
    return userId;
  }

  private extractNickNameFromToken(token: string): string | undefined {
    const parsedPayload = this.decodeJwtPayload(token);
    const nick = parsedPayload?.nickName ?? parsedPayload?.nickname;
    return typeof nick === 'string' && nick.trim() ? nick.trim() : undefined;
  }

  /**
   * Invalidate any existing session server-side before establishing a new one.
   * Dynamic import avoids a circular dependency with the Redux store / auth slice.
   */
  private async ensureLoggedOutBeforeNewAuth(): Promise<void> {
    if (!this.isAuthenticated()) return;
    const { ensureLoggedOutBeforeNewAuth } = await import('../utils/ensureLoggedOutBeforeNewAuth');
    await ensureLoggedOutBeforeNewAuth();
  }

  async loginWithEmail(data: EmailLoginRequest): Promise<AuthResponse> {
    try {
      await this.ensureLoggedOutBeforeNewAuth();
      const response = await publicAuthAxios.post(buildAuthApiUrl(API_CONFIG.ENDPOINTS.EMAIL_LOGIN), data, {
        headers: AUTH_LOCAL_ONLY_HEADERS,
      });
      const responseData: EmailLoginResponse = response.data;
      
      // DEBUG: Log the full response to check what backend returns
      console.log('🔍 [LOGIN] Full backend response:', JSON.stringify(responseData, null, 2));

      if (responseData.success && responseData.details?.accessToken) {
        const fallbackRole: AuthResponse['role'] =
          data.clientType === 'B'
            ? 'BROKER'
            : 'CUSTOMER';

        const accessToken = responseData.details.accessToken;
        const refreshToken = responseData.details.refreshToken;

        // Extract userId from the JWT token
        const userId = this.extractUserIdFromToken(accessToken);
        
        console.log('🔍 [LOGIN] Extracted userId:', userId);

        const authData = this.persistAuthSession(
          {
            accessToken: accessToken,
            refreshToken: refreshToken,
            email: data.email,
            clientType: data.clientType,
            userId: userId,
            firstName: '',
            lastName: '',
            role: fallbackRole
          },
          {
            clientType: data.clientType,
            fallbackEmail: data.email,
            role: fallbackRole,
          }
        );

        return authData;
      }

      throw new Error(responseData.message || 'Login failed');
    } catch (error: any) {
      console.error('Failed to login with email:', error);

      if (error.response?.data) {
        const errorData: EmailLoginResponse = error.response.data;
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid login credentials');
      }

      if (error.response?.status === 401) {
        throw new Error('Incorrect email or password');
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred during login');
    }
  }

  async resetPassword(data: ForgotPasswordRequest): Promise<void> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.FORGOT_PASSWORD);
      console.log('🔵 Resetting password to URL:', url);
      console.log('🔵 Request data:', { ...data, password: '***', confirmPassword: '***' });
      
      const response = await publicAuthAxios.put(url, data, { headers: AUTH_LOCAL_ONLY_HEADERS });
      const responseData: ForgotPasswordResponse = response.data;
      
      console.log('✅ Password reset successful:', responseData);
      
      if (!responseData.success) {
        throw new Error(responseData.message || 'Password reset failed');
      }
    } catch (error: any) {
      console.error('❌ Failed to reset password:', error);
      console.error('❌ Full error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });

      // Handle specific error responses (same validation as sign-up)
      if (error.response?.data) {
        const errorData: ForgotPasswordResponse = error.response.data;
        
        if (errorData.code === 'U002') {
          throw new Error('Your passwords don\'t match. Please check and try again.');
        } else if (errorData.code === '-1') {
          const backendMessage = errorData.message || '';
          const requirements: string[] = [];

          if (/长度至少|at least 8/i.test(backendMessage)) {
            requirements.push('be at least 8 characters');
          }
          if (/大写|uppercase/i.test(backendMessage)) {
            requirements.push('include an uppercase letter');
          }
          if (/小写|lowercase/i.test(backendMessage)) {
            requirements.push('include a lowercase letter');
          }
          if (/数字|number/i.test(backendMessage)) {
            requirements.push('include a number');
          }

          if (requirements.length) {
            const lastRequirement = requirements.pop();
            const requirementsText = requirements.length
              ? `${requirements.join(', ')}${requirements.length > 1 ? ',' : ''} and ${lastRequirement}`
              : lastRequirement;
            throw new Error(`Password must ${requirementsText}.`);
          }

          // Backend sometimes returns a message that doesn't match our simple parser.
          // Prefer surfacing the backend's original message for accuracy.
          if (backendMessage.trim()) {
            throw new Error(backendMessage);
          }
          throw new Error('Password requirements not met. Please include uppercase, lowercase, number, and at least 8 characters.');
        } else if (errorData.code === 'U016') {
          throw new Error('Your verification code has expired. Please request a new one.');
        } else if (errorData.message) {
          throw new Error(errorData.message);
        }
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid reset data');
      } else if (error.response?.status === 404) {
        throw new Error('Email not found. Please check your email address.');
      } else if (error.response) {
        throw new Error(error.response.data?.message || 'Password reset failed');
      } else if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      } else {
        throw new Error(error.message || 'An unexpected error occurred during password reset');
      }
    }
  }

  async verifyGoogleToken(idToken: string, mode: 'login' | 'signup', clientType: ClientTypeCode = 'C'): Promise<AuthResponse> {
    try {
      await this.ensureLoggedOutBeforeNewAuth();
      console.log('🔵 [GOOGLE] Sending Google token to backend for verification...');
      const response = await publicAuthAxios.post(
        buildAuthApiUrl(API_CONFIG.ENDPOINTS.GOOGLE_VERIFY),
        {
          idToken,
          clientType: clientType,
          mode,
        },
        { headers: AUTH_LOCAL_ONLY_HEADERS }
      );

      const responseData: GoogleVerifyResponse = response.data;
      console.log('🔵 [GOOGLE] Backend response:', JSON.stringify(responseData, null, 2));

      if (responseData.success && responseData.details?.accessToken) {
        const details = responseData.details;
        
        // Extract userId from the JWT token (same as email login)
        const userId = this.extractUserIdFromToken(details.accessToken);
        console.log('🔍 [GOOGLE] Extracted userId from token:', userId);
        console.log('🔍 [GOOGLE] Backend provided userId:', details.userId);
        console.log('🔍 [GOOGLE] Using userId:', userId || details.userId || 0);
        
        const authData = this.persistAuthSession(
          {
            accessToken: details.accessToken,
            refreshToken: details.refreshToken,
            email: details.email,
            firstName: details.firstName,
            lastName: details.lastName,
            userId: userId || details.userId || '0',
            role: details.role,
            clientType: details.clientType ?? clientType,
          },
          {
            clientType: details.clientType ?? clientType,
            fallbackEmail: details.email,
            role: details.role,
          }
        );

        console.log('✅ [GOOGLE] Login successful, authData:', {
          userId: authData.userId,
          email: authData.email,
          role: authData.role
        });

        return authData;
      }

      throw new Error(responseData.message || 'Google verification failed');
    } catch (error: any) {
      console.error('Failed to verify Google token:', error);

      if (error.response?.data) {
        const errorData: GoogleVerifyResponse = error.response.data;
        if (errorData.message) {
          throw new Error(errorData.message);
        }
      }

      if (error.response?.status === 401) {
        throw new Error('Google token is invalid or expired. Please try again.');
      }

      if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      }

      throw new Error(error.message || 'An unexpected error occurred during Google verification');
    }
  }

  async completeGoogleAuth(
    details: GoogleVerifyResponseDetails,
    options: {
      clientType?: AuthResponse['clientType'] | ClientTypeCode;
      fallbackEmail?: string;
      role?: AuthResponse['role'];
    } = {}
  ): Promise<AuthResponse> {
    await this.ensureLoggedOutBeforeNewAuth();
    return this.persistAuthSession(details, {
      clientType: options.clientType ?? details.clientType ?? 'C',
      fallbackEmail: options.fallbackEmail ?? details.email,
      role: options.role ?? details.role,
    });
  }

  // Google OAuth methods
  async initiateGoogleLogin(mode: 'login' | 'signup'): Promise<any> {
    try {
      console.log('🔵 [GOOGLE OAUTH] Initiating Google OAuth:', mode);
      
      // Use buildAuthApiUrl to get proper proxy URL in development
      const proxyUrl = buildAuthApiUrl(API_CONFIG.ENDPOINTS.GOOGLE_LOGIN, {
        clientType: 'C',
        mode,
      });
      console.log('🔵 [GOOGLE OAUTH] Proxy URL:', proxyUrl);
      
      // Also prepare full backend URL for popup redirect if needed
      const fullBackendUrl = new URL(`${API_CONFIG.AUTH_BASE_URL}${API_CONFIG.ENDPOINTS.GOOGLE_LOGIN}`);
      fullBackendUrl.searchParams.set('clientType', 'C');
      fullBackendUrl.searchParams.set('mode', mode);
      console.log('🔵 [GOOGLE OAUTH] Full backend URL (for popup):', fullBackendUrl.toString());
      
      // STRATEGY 1: Try to get Google OAuth URL from backend via AJAX (through proxy)
      try {
        const response = await publicAuthAxios.get(proxyUrl, { headers: AUTH_LOCAL_ONLY_HEADERS });
        console.log('✅ [GOOGLE OAUTH] Backend response:', response.data);
        console.log('✅ [GOOGLE OAUTH] Response type:', typeof response.data);
        
        // If backend returns a Google OAuth URL, return it so we can open in popup
        if (typeof response.data === 'string' && response.data.includes('google')) {
          console.log('✅ [GOOGLE OAUTH] Got Google OAuth URL from backend!');
          try {
            const targetUrl = new URL(response.data);
            targetUrl.searchParams.set('clientType', 'C');
            targetUrl.searchParams.set('mode', mode);
            return { type: 'url', url: targetUrl.toString() };
          } catch (urlError) {
            console.warn('⚠️ [GOOGLE OAUTH] Unable to parse backend URL, returning raw:', urlError);
            return { type: 'url', url: `${response.data}${response.data.includes('?') ? '&' : '?'}clientType=C&mode=${mode}` };
          }
        } else if (response.data === 1) {
          console.log('⚠️ [GOOGLE OAUTH] Backend returned "1" - trying popup redirect to backend URL...');
          return { type: 'redirect', url: fullBackendUrl.toString() };
        } else {
          console.log('✅ [GOOGLE OAUTH] Backend returned:', response.data);
          return { type: 'data', data: response.data };
        }
      } catch (ajaxError: any) {
        const status = ajaxError.response?.status;
        console.warn(`⚠️ [GOOGLE OAUTH] AJAX failed with ${status}:`, ajaxError.message);
        
        // STRATEGY 2: For OAuth, 403 or 302 usually means we need direct redirect
        if (status === 403 || status === 302 || status === 401) {
          console.log('🔵 [GOOGLE OAUTH] Trying POPUP REDIRECT to backend (backend will redirect to Google)');
          return { type: 'redirect', url: fullBackendUrl.toString() };
        }
        
        // For other errors, throw
        throw ajaxError;
      }
    } catch (error: any) {
      console.error('❌ [GOOGLE OAUTH] Failed:', error);
      console.error('❌ [GOOGLE OAUTH] Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
      throw error;
    }
  }
  
  // Helper: Open Google OAuth in popup window
  openGoogleOAuthPopup(url: string): Promise<any> {
    return new Promise((resolve, reject) => {
      console.log('🔵 [POPUP] Opening Google OAuth popup:', url);
      
      // Popup window dimensions
      const width = 500;
      const height = 600;
      const left = window.screen.width / 2 - width / 2;
      const top = window.screen.height / 2 - height / 2;
      
      const popup = window.open(
        url,
        'Google OAuth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
      );
      
      if (!popup) {
        console.error('❌ [POPUP] Popup blocked by browser!');
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }
      
      console.log('✅ [POPUP] Popup opened successfully');
      
      // Poll to detect when popup closes
      const pollTimer = setInterval(() => {
        if (popup.closed) {
          clearInterval(pollTimer);
          console.log('🔵 [POPUP] Popup closed');
          // TODO: Check if user was authenticated (could check localStorage, etc.)
          resolve({ closed: true });
        }
      }, 500);
      
      // Listen for messages from popup (OAuth callback could post message)
      const messageHandler = (event: MessageEvent) => {
        // Verify origin for security
        if (event.origin !== window.location.origin) {
          return;
        }
        
        console.log('🔵 [POPUP] Received message from popup:', event.data);
        
        if (event.data.type === 'oauth_success') {
          clearInterval(pollTimer);
          popup.close();
          window.removeEventListener('message', messageHandler);
          resolve(event.data);
        } else if (event.data.type === 'oauth_error') {
          clearInterval(pollTimer);
          popup.close();
          window.removeEventListener('message', messageHandler);
          reject(new Error(event.data.error));
        }
      };
      
      window.addEventListener('message', messageHandler);
      
      // Timeout after 5 minutes
      setTimeout(() => {
        if (!popup.closed) {
          clearInterval(pollTimer);
          popup.close();
          window.removeEventListener('message', messageHandler);
          reject(new Error('OAuth timeout - popup was open too long'));
        }
      }, 5 * 60 * 1000);
    });
  }

  async checkGoogleOAuthHealth(): Promise<any> {
    try {
      const url = buildAuthApiUrl(API_CONFIG.ENDPOINTS.GOOGLE_HEALTH);
      console.log('🔵 [TEST] Checking Google OAuth health:', url);
      const response = await publicAuthAxios.get(url);
      console.log('✅ [TEST] Google health response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('❌ [TEST] Google health check failed:', error);
      throw error;
    }
  }

  async refreshAccessToken(): Promise<AuthResponse> {
    try {
      // Get refresh token from storage
      const refreshToken = secureStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      console.log('🔄 [REFRESH] Attempting to refresh access token...');
      // Use the correct refresh token endpoint
      const candidatePaths = [
        '/auth/refresh/token',     // Primary auth service refresh endpoint
      ];

      let responseData: RefreshTokenResponse | null = null;
      let lastError: any = null;

      for (const path of candidatePaths) {
        try {
          const url = path.startsWith('/auth/')
            ? buildAuthApiUrl(path)
            : buildAuthApiUrl(path); // keep using auth base; PMS proxy also mounts under /api/v1/user
          
      const response = await axios.post(
            url,
        null,  // No body required
        {
          headers: {
                'X-Refresh-Token': refreshToken,
              },
              timeout: 15000,
        }
      );
          responseData = response.data as RefreshTokenResponse;
          break;
        } catch (err) {
          lastError = err;
          continue;
        }
      }

      if (!responseData) {
        throw lastError || new Error('Token refresh failed (no response)');
      }
      
      console.log('✅ [REFRESH] Token refresh successful');

      if (responseData.success && responseData.details?.accessToken) {
        // Get current user data to preserve it
        const currentUser = this.getCurrentUser();
        
        // Reuse persistAuthSession to store tokens and user data
        const authData = this.persistAuthSession(
          {
            accessToken: responseData.details.accessToken,
            refreshToken: responseData.details.refreshToken,  // New refresh token (if rotated)
            email: currentUser?.email,
            firstName: currentUser?.firstName,
            lastName: currentUser?.lastName,
            userId: currentUser?.userId,
            role: currentUser?.role,
            clientType: currentUser?.clientType,
          },
          {
            clientType: currentUser?.clientType || 'C',
            fallbackEmail: currentUser?.email,
            role: currentUser?.role,
          }
        );

        return authData;
      }

      throw new Error(responseData.message || 'Token refresh failed');
    } catch (error: any) {
      console.error('❌ [REFRESH] Token refresh failed:', error);
      
      // If it's a 401, refresh token is expired
      if (error.response?.status === 401) {
        throw new Error('Refresh token expired');
      }
      
      throw new Error(error.message || 'Failed to refresh access token');
    }
  }

  async completeThirdPartyRegistration(
    data: CompleteThirdPartyRegistrationRequest,
    temporaryToken: string
  ): Promise<void> {
    try {
      const url = buildAuthApiUrl('/auth/email/thirdParty/complete/info');
      console.log('🔵 [THIRD-PARTY] Completing third-party registration:', url);
      console.log('🔵 [THIRD-PARTY] Request data:', { nickname: data.nickname, password: '***', confirmPassword: '***' });

      const response = await publicAuthAxios.put(url, data, {
        headers: {
          Authorization: `Bearer ${temporaryToken}`,
        },
        preserveAuthorization: true,
      } as any);

      const responseData: CompleteThirdPartyRegistrationResponse = response.data;
      
      console.log('✅ [THIRD-PARTY] Registration completion successful:', responseData);

      if (!responseData.success) {
        throw new Error(responseData.message || 'Registration completion failed');
      }
      
      // Profile updated successfully - no tokens returned
      // User can log in later with their new password
    } catch (error: any) {
      console.error('❌ [THIRD-PARTY] Failed to complete registration:', error);
      console.error('❌ [THIRD-PARTY] Full error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });

      // Handle specific error responses
      if (error.response?.data) {
        const errorData: CompleteThirdPartyRegistrationResponse = error.response.data;
        
        if (errorData.code === 'U002') {
          throw new Error('Your passwords don\'t match. Please check and try again.');
        } else if (errorData.code === '-1') {
          const backendMessage = errorData.message || '';
          const requirements: string[] = [];

          if (/长度至少|at least 8/i.test(backendMessage)) {
            requirements.push('be at least 8 characters');
          }
          if (/大写|uppercase/i.test(backendMessage)) {
            requirements.push('include an uppercase letter');
          }
          if (/小写|lowercase/i.test(backendMessage)) {
            requirements.push('include a lowercase letter');
          }
          if (/数字|number/i.test(backendMessage)) {
            requirements.push('include a number');
          }

          if (requirements.length) {
            const lastRequirement = requirements.pop();
            const requirementsText = requirements.length
              ? `${requirements.join(', ')}${requirements.length > 1 ? ',' : ''} and ${lastRequirement}`
              : lastRequirement;
            throw new Error(`Password must ${requirementsText}.`);
          }

          // Backend sometimes returns a message that doesn't match our simple parser.
          // Prefer surfacing the backend's original message for accuracy.
          if (backendMessage.trim()) {
            throw new Error(backendMessage);
          }
          throw new Error('Password requirements not met. Please include uppercase, lowercase, number, and at least 8 characters.');
        } else if (errorData.message) {
          throw new Error(errorData.message);
        }
      }

      if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid registration data');
      } else if (error.response?.status === 401) {
        throw new Error('Your registration link has expired or is invalid. Please request a new one.');
      } else if (error.response) {
        throw new Error(error.response.data?.message || 'Registration completion failed');
      } else if (error.request) {
        throw new Error('Network error: Unable to reach the server');
      } else {
        throw new Error(error.message || 'An unexpected error occurred during registration');
      }
    }
  }
}

const authService = new AuthService();
export default authService;
